import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import SEO from '../components/SEO';
import AdComponent from '../components/AdComponent';
import HilltopBanner from '../components/HilltopBanner';

function ArticlesListing() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [articlesPerPage] = useState(6); // 6 articles per page

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await apiService.getArticles();
        if (response.success) {
          setArticles(response.data);
          //console.log("Articles loaded:", response.data);
        }
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }   
    };

    fetchArticles();
  }, []);

  // Calculate pagination
  const indexOfLastArticle = currentPage * articlesPerPage;
  const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
  const currentArticles = articles.slice(indexOfFirstArticle, indexOfLastArticle);
  const totalPages = Math.ceil(articles.length / articlesPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-cyan-50 py-12">
      <SEO
          title="CalcBlog - Articles"
          description="Your destination for helpful calculators and insightful articles. Making complex calculations simple and accessible."
          keywords="calcblog, online calculators, health calculators, bmi calculator, calorie calculator, sleep optimization calculator, carbon footprint calculator, monthly budget calculator, personal finance calculator, sustainability calculators, exosome skincare calculator, ai powered learning tools, interactive calculators, free online calculators, educational calculators, calculator blog, best calculators online, calculator for students, calculator for health, calculator for finance, calculator for environment"
          toolType="blog"
       />
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-800 mb-4 dark:text-slate-200">
            All Articles
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto dark:text-slate-400">
            Browse through all our insightful articles and tutorials
          </p>
        </div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {currentArticles.map(article => (
            <div key={article.id} className="article-card group hover:border-orange-500 transition-all duration-500">
              <div className="flex justify-between items-start mb-4">
                <span className="text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                  {new Date(article.created_at).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </span>
                {article.calculator_name && ( //className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-cyan-100 text-cyan-800">
                  <span className="inline-flex calculator-tag group-hover:scale-105 transition-transform duration-300">
                    🧮 Calculator
                  </span>
                )}
              </div>
              
              <h3 className="text-2xl font-bold text-slate-800 mb-4 group-hover:text-orange-500 transition-colors duration-300 leading-tight dark:text-slate-200 dark:group-hover:text-orange-400">
                {article.title}
              </h3>
              
              <p className="text-slate-600 mb-6 leading-relaxed line-clamp-3 dark:text-slate-300">
                {article.meta_description}
              </p>
              
              <Link 
                to={`/articles/${article.slug}`}
                className="inline-flex items-center text-slate-800 group-hover:text-orange-600 font-semibold group-hover:translate-x-2 transition-all duration-300 dark:text-slate-200 dark:group-hover:text-orange-500"
              >
                Read Article
                <svg className="w-5 h-5 ml-2 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-2">
            {/* Previous Button */}
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                currentPage === 1
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-orange-500 text-white hover:bg-orange-600'
              }`}
            >
              ← Previous
            </button>

            {/* Page Numbers */}
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
              <button
                key={number}
                onClick={() => paginate(number)}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  currentPage === number
                    ? 'bg-orange-500 text-white shadow-lg'
                    : 'bg-white text-slate-700 hover:bg-orange-100 border border-slate-200'
                }`}
              >
                {number}
              </button>
            ))}

            {/* Next Button */}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                currentPage === totalPages
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-orange-500 text-white hover:bg-orange-600'
              }`}
            >
              Next →
            </button>
          </div>
        )}

        {/* Articles Count */}
        <div className="text-center mt-8">
          <p className="text-slate-600">
            Showing {indexOfFirstArticle + 1}-{Math.min(indexOfLastArticle, articles.length)} of {articles.length} articles
          </p>
          <div className="h-64 mt-8 flex justify-center">
               {/*<AdComponent />*/}
               <HilltopBanner />
           </div>  
        </div>

        {/* Empty State */}
        {articles.length === 0 && (
          <div className="text-center py-12">
            <div className="text-slate-400 text-6xl mb-4">📝</div>
            <p className="text-slate-500 text-lg">No articles published yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ArticlesListing;